// src/app/components/settings-page.tsx
import { useMemo, useState, useEffect, useRef } from "react";
import { AIAssistantSidebar } from "./ai-assistant-sidebar";
import { Toggle } from "./ui/toggle";
import { useTheme } from "../contexts/theme-context";
import { useSettings } from "../hooks/common/useSettings";
import { applyFontScaleVars } from "./helper/user-settings-apply";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { PasswordChangeCard } from "./password-change-card";
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Eye,
  Palette,
  CreditCard,
  Search,
  ChevronRight,
  AlertTriangle,
  Check,
  Loader2,
  Clock,
  Columns2,
  LayoutList,
  RotateCcw,
  PanelLeft,
  X,
} from "lucide-react";

interface SettingsPageProps {
  isSidebarCollapsed: boolean;
  onSidebarToggle: (collapsed: boolean) => void;
}

// ── Font slider config ──────────────────────────────────────
const FONT_MIN = 12;
const FONT_MAX = 22;
const FONT_STEP = 0.1;
const FONT_DEFAULT = 14;
const FONT_SAVE_DEBOUNCE_MS = 400;

function AIContextualCard({
  title,
  description,
  type,
}: {
  title: string;
  description: string;
  type: "tip" | "insight" | "warning";
}) {
  const borderColors = {
    tip: "var(--signal-blue)",
    insight: "var(--spectral-violet)",
    warning: "var(--infrared-coral)",
  };
  const bgColors = {
    tip: "var(--signal-blue-5)",
    insight: "var(--spectral-violet-5)",
    warning: "var(--infrared-coral-5)",
  };
  return (
    <div
      style={{
        backgroundColor: bgColors[type],
        borderLeft: `3px solid ${borderColors[type]}`,
        borderRadius: "0 12px 12px 0",
        padding: "20px",
        boxShadow: "var(--elevation-sm)",
      }}
    >
      <h4 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", marginBottom: "8px" }}>
        {title}
      </h4>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: "1.5" }}>
        {description}
      </p>
    </div>
  );
}

export function SettingsPage({ isSidebarCollapsed, onSidebarToggle }: SettingsPageProps) {
  const [activeCategory, setActiveCategory] = useState<
    "profile" | "accessibility" | "privacy" | "appearance" | "billing"
  >("profile");

  const [searchQuery, setSearchQuery] = useState("");
  const { theme: currentTheme, setTheme: setGlobalTheme } = useTheme();
  const { settings, isLoading, error, updateProfile, updatePrivacy, updateAccessibility, updateAppearance } =
    useSettings();

  const [draftProfile, setDraftProfile] = useState<any>(null);
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  // ── Responsive ───────────────────────────────────────────
  const { isDesktop, isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer when switching to desktop
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Body scroll lock while drawer is open
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // ── Font slider local state (for immediate feedback) ────
  const [fontSizeLocal, setFontSizeLocal] = useState<number>(FONT_DEFAULT);
  const fontSizeLocalRef = useRef<number>(FONT_DEFAULT);
  const fontSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedFontRef = useRef<number>(FONT_DEFAULT);

  useEffect(() => {
    fontSizeLocalRef.current = fontSizeLocal;
  }, [fontSizeLocal]);

  // Sync local slider with server state once settings load / refresh
  useEffect(() => {
    if (!settings) return;
    const saved = Number(settings.accessibility?.fontSize ?? FONT_DEFAULT);
    const clamped = Math.min(FONT_MAX, Math.max(FONT_MIN, saved));
    setFontSizeLocal(clamped);
    lastSavedFontRef.current = clamped;
  }, [settings?.accessibility?.fontSize]);

  // Flush pending font save when unmounting
  useEffect(() => {
    return () => {
      if (fontSaveTimerRef.current) {
        clearTimeout(fontSaveTimerRef.current);
        fontSaveTimerRef.current = null;
        const val = fontSizeLocalRef.current;
        if (Math.abs(val - lastSavedFontRef.current) >= 0.05) {
          updateAccessibility({ fontSize: val }).catch(() => {});
          lastSavedFontRef.current = val;
        }
      }
    };
  }, [updateAccessibility]);

  const handleFontChange = (nextRaw: number) => {
    const next = Math.min(FONT_MAX, Math.max(FONT_MIN, Number(nextRaw) || FONT_DEFAULT));
    const snapped = Math.round(next * 10) / 10;

    setFontSizeLocal(snapped);
    applyFontScaleVars(snapped);

    if (fontSaveTimerRef.current) clearTimeout(fontSaveTimerRef.current);
    fontSaveTimerRef.current = setTimeout(() => {
      fontSaveTimerRef.current = null;
      if (Math.abs(snapped - lastSavedFontRef.current) >= 0.05) {
        updateAccessibility({ fontSize: snapped }).catch(() => {});
        lastSavedFontRef.current = snapped;
      }
    }, FONT_SAVE_DEBOUNCE_MS);
  };

  const resetFont = () => handleFontChange(FONT_DEFAULT);

  const categories = useMemo(
    () => [
      { id: "profile" as const, label: "Profile & Account", icon: User, color: "var(--spectral-violet)" },
      { id: "accessibility" as const, label: "Accessibility", icon: Eye, color: "var(--signal-blue)" },
      { id: "privacy" as const, label: "Data & Privacy", icon: Shield, color: "var(--infrared-coral)" },
      { id: "appearance" as const, label: "Appearance", icon: Palette, color: "var(--chart-1)" },
      { id: "billing" as const, label: "Billing & Plan", icon: CreditCard, color: "var(--chart-4)" },
    ],
    []
  );

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.label.toLowerCase().includes(q));
  }, [categories, searchQuery]);

  const activeCategoryMeta = useMemo(
    () => categories.find((c) => c.id === activeCategory),
    [categories, activeCategory]
  );

  useEffect(() => {
    if (!settings) return;
    setDraftProfile({ ...settings.profile });
    setProfileDirty(false);
  }, [
    settings?.profile?.fullName,
    settings?.profile?.email,
    settings?.profile?.jobTitle,
    settings?.profile?.company,
    settings?.profile?.timezone,
    settings?.profile?.language,
  ]);

  useEffect(() => {
    if (!settings) return;
    const desired = (settings.appearance?.theme || "light") as "light" | "dark";
    if (desired !== currentTheme) setGlobalTheme(desired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.appearance?.theme]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "var(--background)" }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: "var(--muted-foreground)" }} />
          <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "var(--background)" }}>
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4" style={{ color: "var(--infrared-coral)" }} />
          <p style={{ color: "var(--foreground)", fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)", marginBottom: "8px" }}>
            Failed to load settings
          </p>
          <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>{error?.message || "An error occurred"}</p>
        </div>
      </div>
    );
  }

  const saveProfile = async () => {
    if (!draftProfile) return;
    setProfileSaving(true);
    try {
      await updateProfile(draftProfile);
      setProfileDirty(false);
    } finally {
      setProfileSaving(false);
    }
  };

  const resetProfile = () => {
    setDraftProfile({ ...settings.profile });
    setProfileDirty(false);
  };

  const currentInboxLayout = (settings.appearance?.inboxLayout === "accordion" ? "accordion" : "split") as
    | "split"
    | "accordion";

  const fontIsCustom = Math.abs(fontSizeLocal - FONT_DEFAULT) >= 0.05;

  // ── Responsive style tokens ─────────────────────────────
  const mainPad = isMobile ? 20 : isDesktop ? 40 : 28;
  const heroPad = isMobile ? 24 : 32;
  const cardBodyPad = isMobile ? 20 : 32;
  const cardHeaderPad = isMobile ? "16px 20px" : "20px 24px";
  const desktopRightMargin = isDesktop ? "64px" : "0px";

  // ── Settings nav content (rendered in aside on desktop, drawer on mobile) ─
  const navContent = (
    <>
      <div
        style={{
          height: "89px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          padding: "24px 24px 1px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "var(--alpha-teal-10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SettingsIcon style={{ width: "16px", height: "16px", color: "var(--alpha-teal)" }} />
          </div>
          <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>Settings</h2>
          {!isDesktop && (
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close settings menu"
              style={{
                marginLeft: "auto",
                padding: 6,
                borderRadius: 7,
                border: "1px solid var(--border)",
                backgroundColor: "var(--muted)",
                cursor: "pointer",
                color: "var(--muted-foreground)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>
      </div>

      <nav style={{ padding: "0", flex: 1, paddingTop: "16px", overflowY: "auto" }}>
        {filteredCategories.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setDrawerOpen(false);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 24px",
                backgroundColor: isActive ? "var(--muted)" : "transparent",
                border: "none",
                borderLeft: isActive ? `3px solid ${category.color}` : "3px solid transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                textAlign: "left",
              }}
            >
              <Icon
                style={{
                  width: "18px",
                  height: "18px",
                  color: isActive ? category.color : "var(--muted-foreground)",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: isActive ? "var(--font-weight-semibold)" : "var(--font-weight-medium)",
                  color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {category.label}
              </span>
              {isActive && (
                <ChevronRight
                  style={{ width: "16px", height: "16px", color: category.color, flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </nav>

      <div style={{ padding: "16px 24px 24px", flexShrink: 0 }}>
        <div style={{ position: "relative" }}>
          <Search
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "16px",
              height: "16px",
              color: "var(--muted-foreground)",
            }}
          />
          <input
            type="search"
            placeholder="Search settings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              paddingLeft: "36px",
              paddingRight: "12px",
              paddingTop: "10px",
              paddingBottom: "10px",
              backgroundColor: "var(--input-background)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      {/* Settings Nav — inline aside on desktop, off-canvas drawer on mobile/tablet */}
      {isDesktop ? (
        <aside
          style={{
            width: "280px",
            borderRight: "1px solid var(--border)",
            backgroundColor: "var(--background)",
            minHeight: "100vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          {navContent}
        </aside>
      ) : (
        <>
          {/* Backdrop */}
          {drawerOpen && (
            <div
              onClick={() => setDrawerOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 45,
                transition: "opacity 300ms ease-in-out",
              }}
              aria-hidden="true"
            />
          )}
          {/* Drawer */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100dvh",
              width: "min(320px, 85vw)",
              zIndex: 46,
              backgroundColor: "var(--background)",
              borderRight: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 300ms ease-in-out",
              boxShadow: drawerOpen ? "0 0 30px rgba(0,0,0,0.15)" : "none",
            }}
            aria-hidden={!drawerOpen}
          >
            {navContent}
          </div>
        </>
      )}

      {/* Content */}
      <main
        className="flex-1 transition-all duration-300"
        style={{
          padding: `${mainPad}px`,
          // On desktop, main has its own scroll region so the fixed-width aside
          // stays put. On mobile/tablet the aside is an overlay drawer, so let
          // content flow naturally and use the page/app-shell's scroll context.
          overflowY: isDesktop ? "auto" : "visible",
          minHeight: isDesktop ? "100vh" : "auto",
          backgroundColor: "var(--background)",
          marginRight: desktopRightMargin,
          minWidth: 0,
        }}
      >
        {/* Mobile top strip — PanelLeft toggle + current category breadcrumb */}
        {!isDesktop && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 20,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted-30)",
            }}
          >
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open settings categories"
              title="Settings categories"
              style={{
                padding: 8,
                borderRadius: 8,
                border: "1px solid var(--border)",
                backgroundColor: "var(--background)",
                cursor: "pointer",
                color: "var(--foreground)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PanelLeft style={{ width: 16, height: 16 }} />
            </button>
            {activeCategoryMeta && (
              <>
                <activeCategoryMeta.icon
                  style={{
                    width: 16,
                    height: 16,
                    color: activeCategoryMeta.color,
                    flexShrink: 0,
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--foreground)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeCategoryMeta.label}
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ width: "100%" }}>
          {/* PROFILE */}
          {activeCategory === "profile" && draftProfile && (
            <>
              {/* Hero — stacks icon-above-title on mobile */}
              <div
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: `${heroPad}px`,
                  marginBottom: "32px",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "start",
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: "var(--spectral-violet-10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <User style={{ width: "32px", height: "32px", color: "var(--spectral-violet)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        marginBottom: "12px",
                        fontSize: isMobile ? "clamp(20px, 5vw, 24px)" : undefined,
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Profile & Account Settings
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
                        color: "var(--muted-foreground)",
                        lineHeight: "1.6",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Update your profile. Changes are saved only when you click <strong>Save Changes</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: "20px",
                  marginBottom: "32px",
                }}
              >
                <AIContextualCard title="Draft Mode" description="Edits are kept locally until you click Save Changes." type="insight" />
                <AIContextualCard title="Reset" description="Use Reset to discard unsaved edits and revert to your last saved profile." type="tip" />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  <div
                    style={{
                      padding: cardHeaderPad,
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: "var(--muted-30)",
                    }}
                  >
                    <h3 style={{ fontSize: "var(--text-xl)" }}>Personal Information</h3>
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: "4px" }}>
                      Update your profile details
                    </p>
                  </div>

                  <div style={{ padding: `${cardBodyPad}px` }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                      <div>
                        <label style={{ display: "block", marginBottom: "8px" }}>Full Name</label>
                        <input
                          type="text"
                          value={draftProfile.fullName}
                          onChange={(e) => {
                            setDraftProfile((p: any) => ({ ...p, fullName: e.target.value }));
                            setProfileDirty(true);
                          }}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            backgroundColor: "var(--input-background)",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-base)",
                            color: "var(--foreground)",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: "block", marginBottom: "8px" }}>Email Address</label>
                        <div style={{ position: "relative" }}>
                          <input
                            type="email"
                            value={draftProfile.email}
                            onChange={(e) => {
                              setDraftProfile((p: any) => ({ ...p, email: e.target.value }));
                              setProfileDirty(true);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              paddingRight: draftProfile.emailVerified ? "92px" : "16px",
                              backgroundColor: "var(--input-background)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                              color: "var(--foreground)",
                              boxSizing: "border-box",
                            }}
                          />
                          {draftProfile.emailVerified && (
                            <span
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "var(--text-xs)",
                                color: "var(--success-text)",
                                fontWeight: "var(--font-weight-medium)",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                              }}
                            >
                              <Check style={{ width: "12px", height: "12px" }} />
                              Verified
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Job + Company — stacks on mobile */}
                      <div
                        className="grid grid-cols-1 md:grid-cols-2"
                        style={{ gap: "16px" }}
                      >
                        <div>
                          <label style={{ display: "block", marginBottom: "8px" }}>Job Title</label>
                          <input
                            type="text"
                            value={draftProfile.jobTitle}
                            onChange={(e) => {
                              setDraftProfile((p: any) => ({ ...p, jobTitle: e.target.value }));
                              setProfileDirty(true);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              backgroundColor: "var(--input-background)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                              color: "var(--foreground)",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", marginBottom: "8px" }}>Company</label>
                          <input
                            type="text"
                            value={draftProfile.company}
                            onChange={(e) => {
                              setDraftProfile((p: any) => ({ ...p, company: e.target.value }));
                              setProfileDirty(true);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              backgroundColor: "var(--input-background)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                              color: "var(--foreground)",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>

                      {/* Timezone + Language — stacks on mobile */}
                      <div
                        className="grid grid-cols-1 md:grid-cols-2"
                        style={{ gap: "16px" }}
                      >
                        <div>
                          <label style={{ display: "block", marginBottom: "8px" }}>Time Zone</label>
                          <select
                            value={draftProfile.timezone}
                            onChange={(e) => {
                              setDraftProfile((p: any) => ({ ...p, timezone: e.target.value }));
                              setProfileDirty(true);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              backgroundColor: "var(--input-background)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                              color: "var(--foreground)",
                              boxSizing: "border-box",
                            }}
                          >
                            <option>UTC-8 (Pacific Time)</option>
                            <option>UTC-5 (Eastern Time)</option>
                            <option>UTC+0 (UTC)</option>
                            <option>UTC+1 (Central European)</option>
                          </select>
                        </div>

                        <div>
                          <label style={{ display: "block", marginBottom: "8px" }}>Language</label>
                          <select
                            value={draftProfile.language}
                            onChange={(e) => {
                              setDraftProfile((p: any) => ({ ...p, language: e.target.value }));
                              setProfileDirty(true);
                            }}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              backgroundColor: "var(--input-background)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                              color: "var(--foreground)",
                              boxSizing: "border-box",
                            }}
                          >
                            <option>English (US)</option>
                            <option>English (UK)</option>
                            <option>Spanish</option>
                            <option>French</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: "28px", paddingTop: "18px", borderTop: "1px solid var(--border)" }}>
                      <div
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      >
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          Your changes will be saved only when you click <strong>Save Changes</strong>.
                        </p>

                        <div className="flex gap-2.5 w-full sm:w-auto">
                          <button
                            disabled={!profileDirty || profileSaving}
                            onClick={resetProfile}
                            className="flex-1 sm:flex-initial"
                            style={{
                              padding: "10px 14px",
                              backgroundColor: "transparent",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              cursor: !profileDirty ? "not-allowed" : "pointer",
                              opacity: !profileDirty ? 0.6 : 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Reset
                          </button>
                          <button
                            disabled={!profileDirty || profileSaving}
                            onClick={saveProfile}
                            className="flex-1 sm:flex-initial"
                            style={{
                              padding: "10px 14px",
                              backgroundColor: !profileDirty ? "var(--muted)" : "var(--accent)",
                              color: !profileDirty ? "var(--muted-foreground)" : "var(--accent-foreground)",
                              border: "none",
                              borderRadius: "var(--radius)",
                              cursor: !profileDirty ? "not-allowed" : "pointer",
                              fontWeight: "var(--font-weight-semibold)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {profileSaving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Password — handled by self-contained component */}
              <PasswordChangeCard
                cardHeaderPad={cardHeaderPad}
                cardBodyPad={cardBodyPad}
              />
            </>
          )}

          {/* ACCESSIBILITY */}
          {activeCategory === "accessibility" && (
            <>
              <div
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: `${heroPad}px`,
                  marginBottom: "32px",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "start",
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: "var(--signal-blue-10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Eye style={{ width: "32px", height: "32px", color: "var(--signal-blue)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        marginBottom: "12px",
                        fontSize: isMobile ? "clamp(20px, 5vw, 24px)" : undefined,
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Accessibility Settings
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
                        color: "var(--muted-foreground)",
                        lineHeight: "1.6",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Customize theme, text size, contrast, and motion preferences.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  <div
                    style={{
                      padding: cardHeaderPad,
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: "var(--muted-30)",
                    }}
                  >
                    <h3 style={{ fontSize: "var(--text-xl)" }}>Visual Accessibility</h3>
                  </div>

                  <div style={{ padding: `${cardBodyPad}px` }}>
                    <label style={{ display: "block", marginBottom: "12px" }}>Theme</label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "12px",
                        marginBottom: "28px",
                      }}
                    >
                      {(["light", "dark"] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => {
                            setGlobalTheme(t);
                            updateAppearance({ theme: t }).catch(() => {});
                          }}
                          style={{
                            padding: "16px",
                            border: currentTheme === t ? "2px solid var(--accent)" : "1px solid var(--border)",
                            backgroundColor: currentTheme === t ? "var(--alpha-teal-5)" : "var(--background)",
                            borderRadius: "var(--radius)",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <p
                            style={{
                              fontWeight: "var(--font-weight-semibold)",
                              textTransform: "capitalize",
                              marginBottom: "4px",
                            }}
                          >
                            {t}
                          </p>
                          <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                            {t === "dark" ? "Reduced eye strain" : "Default theme"}
                          </p>
                        </button>
                      ))}
                    </div>

                    {/* ── FONT SIZE — continuous slider ─────────────── */}
                    <div style={{ marginBottom: "28px" }}>
                      <div
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3"
                        style={{ marginBottom: "10px" }}
                      >
                        <label style={{ margin: 0 }}>Font Size</label>

                        <div className="flex items-center gap-2.5 self-start sm:self-auto">
                          <span
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "var(--foreground)",
                              fontWeight: "var(--font-weight-semibold)",
                              fontVariantNumeric: "tabular-nums",
                              minWidth: 48,
                              textAlign: "right",
                            }}
                          >
                            {fontSizeLocal.toFixed(1)}px
                          </span>

                          {fontIsCustom && (
                            <button
                              onClick={resetFont}
                              title={`Reset to default (${FONT_DEFAULT}px)`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "4px 8px",
                                backgroundColor: "transparent",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                cursor: "pointer",
                                fontSize: "var(--text-xs)",
                                color: "var(--muted-foreground)",
                                fontWeight: "var(--font-weight-medium)",
                              }}
                            >
                              <RotateCcw style={{ width: 11, height: 11 }} />
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <input
                        type="range"
                        min={FONT_MIN}
                        max={FONT_MAX}
                        step={FONT_STEP}
                        value={fontSizeLocal}
                        onChange={(e) => handleFontChange(Number(e.target.value))}
                        style={{ width: "100%", accentColor: "var(--accent)", cursor: "pointer" }}
                        aria-label="Font size in pixels"
                      />

                      {/* Tick labels — fewer on mobile to avoid crowding */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "var(--text-xs)",
                          color: "var(--muted-foreground)",
                          marginTop: 6,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {isMobile ? (
                          <>
                            <span>{FONT_MIN}</span>
                            <span>14</span>
                            <span>18</span>
                            <span>{FONT_MAX}</span>
                          </>
                        ) : (
                          <>
                            <span>{FONT_MIN}</span>
                            <span>14</span>
                            <span>16</span>
                            <span>18</span>
                            <span>20</span>
                            <span>{FONT_MAX}</span>
                          </>
                        )}
                      </div>

                      <div
                        style={{
                          marginTop: 14,
                          padding: "12px 14px",
                          backgroundColor: "var(--muted-30)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "var(--text-xs)",
                            color: "var(--muted-foreground)",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontWeight: "var(--font-weight-semibold)",
                            margin: 0,
                            marginBottom: 6,
                          }}
                        >
                          Live preview
                        </p>
                        <p
                          style={{
                            fontSize: "var(--text-base)",
                            color: "var(--foreground)",
                            margin: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          The quick brown fox jumps over the lazy dog.
                        </p>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            margin: 0,
                            marginTop: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          Smaller text scales proportionally — hierarchy is preserved.
                        </p>
                      </div>
                    </div>

                    {/* High Contrast row */}
                    <div
                      className="flex items-center justify-between gap-3"
                      style={{
                        padding: "16px",
                        backgroundColor: "var(--muted-30)",
                        borderRadius: "var(--radius)",
                        border: "1px solid var(--border)",
                        marginBottom: "16px",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: "var(--font-weight-semibold)" }}>High Contrast Mode</p>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            marginTop: "4px",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          Increase contrast for readability
                        </p>
                      </div>
                      <Toggle
                        enabled={settings.accessibility.highContrast}
                        onChange={(v) => updateAccessibility({ highContrast: v }).catch(() => {})}
                        size="md"
                      />
                    </div>

                    {/* Reduce Motion row */}
                    <div
                      className="flex items-center justify-between gap-3"
                      style={{
                        padding: "16px",
                        backgroundColor: "var(--muted-30)",
                        borderRadius: "var(--radius)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: "var(--font-weight-semibold)" }}>Reduce Motion</p>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            marginTop: "4px",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          Minimize animations and transitions
                        </p>
                      </div>
                      <Toggle
                        enabled={settings.accessibility.reducedMotion}
                        onChange={(v) => updateAccessibility({ reducedMotion: v }).catch(() => {})}
                        size="md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* PRIVACY */}
          {activeCategory === "privacy" && (
            <>
              <div
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: `${heroPad}px`,
                  marginBottom: "32px",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "start",
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: "var(--infrared-coral-10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Shield style={{ width: "32px", height: "32px", color: "var(--infrared-coral)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        marginBottom: "12px",
                        fontSize: isMobile ? "clamp(20px, 5vw, 24px)" : undefined,
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Data & Privacy Settings
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
                        color: "var(--muted-foreground)",
                        lineHeight: "1.6",
                      }}
                    >
                      Control how usage analytics is collected.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  <div
                    style={{
                      padding: cardHeaderPad,
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: "var(--muted-30)",
                    }}
                  >
                    <h3 style={{ fontSize: "var(--text-xl)" }}>Data Collection</h3>
                  </div>
                  <div style={{ padding: `${cardBodyPad}px` }}>
                    <div
                      className="flex items-center justify-between gap-3"
                      style={{
                        padding: "16px",
                        backgroundColor: "var(--muted-30)",
                        borderRadius: "var(--radius)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: "var(--font-weight-semibold)" }}>Usage Analytics</p>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            marginTop: "4px",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          Share anonymous usage metrics
                        </p>
                      </div>
                      <Toggle
                        enabled={settings.privacy.usageAnalytics}
                        onChange={(v) => updatePrivacy({ usageAnalytics: v }).catch(() => {})}
                        size="md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* APPEARANCE */}
          {activeCategory === "appearance" && (
            <>
              <div
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: `${heroPad}px`,
                  marginBottom: "32px",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "start",
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: "var(--chart-1-10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Palette style={{ width: "32px", height: "32px", color: "var(--chart-1)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        marginBottom: "12px",
                        fontSize: isMobile ? "clamp(20px, 5vw, 24px)" : undefined,
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Appearance Settings
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
                        color: "var(--muted-foreground)",
                        lineHeight: "1.6",
                      }}
                    >
                      Layout preferences and data display preferences.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  <div
                    style={{
                      padding: cardHeaderPad,
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: "var(--muted-30)",
                    }}
                  >
                    <h3 style={{ fontSize: "var(--text-xl)" }}>Suggested Actions Layout</h3>
                    <p
                      style={{
                        fontSize: "var(--text-sm)",
                        color: "var(--muted-foreground)",
                        marginTop: "4px",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Choose how the Suggested Actions tab displays items. You can also toggle this inline at the top of the inbox.
                    </p>
                  </div>

                  <div style={{ padding: `${cardBodyPad}px` }}>
                    {/* Inbox layout picker — stacks single column on mobile */}
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2"
                      style={{ gap: "12px" }}
                    >
                      <button
                        onClick={() => updateAppearance({ inboxLayout: "split" }).catch(() => {})}
                        style={{
                          padding: "18px",
                          textAlign: "left",
                          border:
                            currentInboxLayout === "split"
                              ? "2px solid var(--accent)"
                              : "1px solid var(--border)",
                          backgroundColor:
                            currentInboxLayout === "split" ? "var(--alpha-teal-5)" : "var(--background)",
                          borderRadius: "var(--radius)",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <Columns2 style={{ width: 18, height: 18, color: "var(--accent)", flexShrink: 0 }} />
                          <p style={{ fontWeight: "var(--font-weight-semibold)" }}>Split view</p>
                        </div>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            lineHeight: 1.5,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          List on the left, full detail panel on the right. Good for fast navigation with constant context.
                        </p>
                      </button>

                      <button
                        onClick={() => updateAppearance({ inboxLayout: "accordion" }).catch(() => {})}
                        style={{
                          padding: "18px",
                          textAlign: "left",
                          border:
                            currentInboxLayout === "accordion"
                              ? "2px solid var(--accent)"
                              : "1px solid var(--border)",
                          backgroundColor:
                            currentInboxLayout === "accordion" ? "var(--alpha-teal-5)" : "var(--background)",
                          borderRadius: "var(--radius)",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <LayoutList style={{ width: 18, height: 18, color: "var(--accent)", flexShrink: 0 }} />
                          <p style={{ fontWeight: "var(--font-weight-semibold)" }}>Accordion view</p>
                        </div>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            lineHeight: 1.5,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          Single column. Tap rows to expand inline — multiple can stay open at once for side-by-side comparison.
                        </p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  <div
                    style={{
                      padding: cardHeaderPad,
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: "var(--muted-30)",
                    }}
                  >
                    <h3 style={{ fontSize: "var(--text-xl)" }}>Navigation Preferences</h3>
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: "4px" }}>
                      Applied after login
                    </p>
                  </div>

                  <div style={{ padding: `${cardBodyPad}px` }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div
                        className="flex items-center justify-between gap-3"
                        style={{
                          padding: "16px",
                          backgroundColor: "var(--muted-30)",
                          borderRadius: "var(--radius)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: "var(--font-weight-semibold)" }}>
                            Start with Navigation Collapsed
                          </p>
                          <p
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "var(--muted-foreground)",
                              marginTop: "4px",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            Collapse left navigation by default
                          </p>
                        </div>
                        <Toggle
                          enabled={settings.appearance.startNavCollapsed}
                          onChange={(v) => updateAppearance({ startNavCollapsed: v }).catch(() => {})}
                          size="md"
                        />
                      </div>

                      <div
                        className="flex items-center justify-between gap-3"
                        style={{
                          padding: "16px",
                          backgroundColor: "var(--muted-30)",
                          borderRadius: "var(--radius)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: "var(--font-weight-semibold)" }}>
                            Start with AI Assistant Collapsed
                          </p>
                          <p
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "var(--muted-foreground)",
                              marginTop: "4px",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            Collapse AI sidebar by default
                          </p>
                        </div>
                        <Toggle
                          enabled={settings.appearance.startAiCollapsed}
                          onChange={(v) => updateAppearance({ startAiCollapsed: v }).catch(() => {})}
                          size="md"
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: "14px",
                        padding: "12px 16px",
                        backgroundColor: "var(--signal-blue-5)",
                        borderLeft: "3px solid var(--signal-blue)",
                        borderRadius: "8px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--foreground)",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        These preferences are applied automatically after login.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* BILLING */}
          {activeCategory === "billing" && (
            <>
              <div
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: `${heroPad}px`,
                  marginBottom: "32px",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "start",
                    gap: isMobile ? "16px" : "24px",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      backgroundColor: "var(--chart-4-10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CreditCard style={{ width: "32px", height: "32px", color: "var(--chart-4)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        marginBottom: "12px",
                        fontSize: isMobile ? "clamp(20px, 5vw, 24px)" : undefined,
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      Billing & Subscription
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
                        color: "var(--muted-foreground)",
                        lineHeight: "1.6",
                      }}
                    >
                      Billing is coming soon.
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  textAlign: "center",
                  padding: isMobile ? "60px 20px" : "80px 40px",
                  backgroundColor: "var(--muted-30)",
                  borderRadius: "12px",
                  border: "1px dashed var(--border)",
                }}
              >
                <Clock
                  style={{
                    width: "48px",
                    height: "48px",
                    color: "var(--muted-foreground)",
                    margin: "0 auto 16px",
                  }}
                />
                <h3 style={{ marginBottom: "8px", color: "var(--foreground)" }}>Coming soon</h3>
                <p
                  style={{
                    fontSize: "var(--text-base)",
                    color: "var(--muted-foreground)",
                    maxWidth: "520px",
                    margin: "0 auto",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  Billing and plan management will be available in a future update.
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      {/* AI Assistant Sidebar — only on desktop */}
      <div className="hidden lg:block">
        <AIAssistantSidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} />
      </div>
    </div>
  );
}